import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  gql,
  NetworkStatus,
  usePaginationHelper,
  useQuery,
  useNavigate,
} from '@common/hooks';
import {
  buildOptionsFromData,
  ErrorHandler,
  FilteredTable,
  Gutter,
  Icon,
  MultiSelectTableFilter,
  TextSearchTableFilter,
} from '@common/components';
import { PAGINATED_STAFFING_REQUEST_TEMPLATES_QUERY } from 'works/legacy/graphql/staffing-request-template';
import { RolesQuery } from '@clinician-profile/graphql';
import { OrganizationUnitType, RoleType } from '@scheduling/graphql';
import { ENUM_QUERY } from '@common/graphql';
import { buildDialogUrl } from '@common/services';

const WORKS_ORGANIZATION_UNIT_QUERY = gql`
  query WorksOrganizationUnits {
    organizationUnits {
      id
      name
    }
  }
`;

export function CreateStaffingRequestSelectTemplateTable({
  searchQuery,
  staffingRequestType,
}: {
  searchQuery: string;
  staffingRequestType: ['long_term', 'on_demand'];
}) {
  const navigate = useNavigate();

  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: 'StaffingRequestSelectTemplateTable',
    defaultPaginationData: {
      defaultPageSize: 10,
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'LOCATION',
    },
    columnsDictionary: {
      name: 'NAME',
      organizationUnit: 'ORGANIZATION_UNIT',
      billingRegularRate: 'BILLING_REGULAR_RATE',
    },
  });

  useEffect(() => {
    filterChangeHandler({
      ...filters,
      search: searchQuery ? [searchQuery] : null,
    });
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    loading: organizationUnitLoading,
    error: organizationUnitError,
    data: organizationUnitData,
  } = useQuery(WORKS_ORGANIZATION_UNIT_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const {
    loading: rolesLoading,
    error: rolesError,
    data: rolesData,
  } = useQuery(RolesQuery);

  const {
    loading: staffingRequestTypeLoading,
    error: staffingRequestTypeError,
    data: staffingRequestTypesData,
  } = useQuery(ENUM_QUERY, {
    variables: { key: 'StaffingRequestTypes' },
  });

  const { loading, error, data, networkStatus } = useQuery(
    PAGINATED_STAFFING_REQUEST_TEMPLATES_QUERY,
    {
      variables: {
        ...paginationVariables,
        staffingRequestTypes: [staffingRequestType],
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const anyError =
    error || organizationUnitError || staffingRequestTypeError || rolesError;

  if (anyError) {
    return (
      <ErrorHandler
        error={anyError}
        log="CreateStaffingRequestSelectTemplateTable"
      />
    );
  }

  const isLoaded = Boolean(networkStatus === NetworkStatus.ready && data);
  const paginationKey = 'paginatedStaffingRequestTemplates';
  const recordsKey = 'staffingRequestTemplates';
  const templates = isLoaded ? data[paginationKey][recordsKey] : [];
  const total = isLoaded ? data[paginationKey]['totalCount'] : null;

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'name',
      key: 'name',
      width: '35%',
      sorter: true,
      filtered: filters.name || false,
      filteredValue: filters.name || null,
      filterDropdown: TextSearchTableFilter,
      render: (name: string) => <b>{name}</b>,
    },
    {
      title: 'Unit',
      dataIndex: 'organizationUnit',
      key: 'organizationUnit',
      width: '30%',
      filtered: filters.organizationUnit || false,
      filteredValue: filters.organizationUnit || null,
      filters: buildOptionsFromData(
        organizationUnitData?.organizationUnits,
        (organizationUnit: OrganizationUnitType) => ({
          value: organizationUnit.id,
          text: organizationUnit.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (organizationUnit: OrganizationUnitType) => (
        <>
          {organizationUnit.name}
          <br />
          <span className="secondary-paragraph">
            {organizationUnit.department.organizationBusiness.name}
          </span>
        </>
      ),
    },
    {
      title: 'Position / Code',
      dataIndex: 'staffingRequest',
      key: 'role',
      width: '20%',
      filtered: filters.role || false,
      filteredValue: filters.role || null,
      filters: buildOptionsFromData(
        rolesData?.roles || [],
        (role: RoleType) => ({
          text: role.abbreviation,
          value: role.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (
        _: unknown,
        record: {
          role: { abbreviation: string };
          externalId: string;
          staffingRequestType: string;
        }
      ) => (
        <>
          {
            staffingRequestTypesData?.enum?.find(
              ({ name }: { name: string }) =>
                name === record.staffingRequestType
            )?.description
          }{' '}
          - {record.role.abbreviation}
          <br />
          <span className="secondary-paragraph">
            {record.externalId || 'No External Job ID assigned'}
          </span>
        </>
      ),
    },
    {
      title: '',
      width: '24px',
      render: () => (
        <div style={{ textAlign: 'right' }}>
          <Icon size="xl" kind="light" icon="angleRight" color="steelDarker" />
        </div>
      ),
    },
  ];

  return (
    <>
      <section className="create-staffing-request-select-template-table">
        <Gutter size="sm">
          <FilteredTable
            rowKey="id"
            filters={filters}
            columns={columns}
            loading={
              loading ||
              organizationUnitLoading ||
              staffingRequestTypeLoading ||
              rolesLoading
            }
            onChange={tableChangeHandler}
            onFilterChange={filterChangeHandler}
            dataSource={templates}
            pagination={{ ...paginationVariables, total }}
            localStorageFilterKey={localStorageFilterKey}
            scroll={false}
            onRow={(record: { id: string }) => ({
              onClick: () => {
                const url = buildTemplateDetailsDrawerUrl(record.id);
                navigate(url);
              },
            })}
          />
        </Gutter>
      </section>
      <style jsx>{`
        .create-staffing-request-select-template-table,
        .create-staffing-request-select-template-table :global(.FilteredTable) {
          width: 100%;
        }
        .create-staffing-request-select-template-table
          :global(.ant-table-cell) {
          padding: 12px;
        }
      `}</style>
    </>
  );
}

function buildTemplateDetailsDrawerUrl(templateId: string) {
  return buildDialogUrl('staffing-request-template-details-drawer', {
    props: { templateId },
  });
}

CreateStaffingRequestSelectTemplateTable.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  staffingRequestType: PropTypes.string.isRequired,
};
